import { useEffect, useMemo, useState, type ComponentType } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import { escapeRegex } from 'lib/string/regexUtils'
import Image from 'components/Common/Image'

import type { MessagePart } from './Message'
import Message from './Message'
import type { FlightSearchDrawerComponentProps } from './ChatFlightSearchContent'
import type { ChatCartComponentProps } from './chatCartTypes'
import { stripContextTags } from 'tripPlanner/utils/contextTag'
import { userMessagePrefersPlainAssistantUi } from 'tripPlanner/utils/plainAskAIAssistantUi'
import AssistantResponseToolbar from './AssistantResponseToolbar'
import { StaffMessageRewindControls, StaffRewindBanner } from './StaffRewindControls'
import AddedItineraryItemLink from './AddedItineraryItemLink'
import { useStaffRewind } from 'tripPlanner/assistant/contexts/StaffRewindContext'
import { filterMessagesUpToStaffRewindPoint } from 'tripPlanner/utils/staffRewindMessages'
import { MESSAGE_ROLES, PART_TYPES, UI_COMPONENT_TYPES } from './constants'
import BodyText from 'LuxKit/Typography/BodyText'
import {
  getChatToolLoadingMessageDescriptor,
  type ChatToolLoadingKey,
} from 'tripPlanner/config/chatToolLoading'
import { EMPTY_SUMMARIES, type ChatLoadingStatus } from 'tripPlanner/assistant/runtime/chatStream'
import { hasAssistantReplyContentStarted } from 'tripPlanner/assistant/runtime/chatStream/chatScrollGeometry'
import { TOOL_NAME } from 'tripPlanner/api/types'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

/** Matches `<Group gap={24}>` below — keep in sync when changing message spacing. */
const MESSAGE_LIST_GAP_PX = 24
const LOADING_AFTER_PLACEHOLDER_GAP_PX = 12
const LOADING_AFTER_CONTENT_GAP_PX = 4
const LOADING_AFTER_PLACEHOLDER_CLASS = 'after-placeholder'

const PlaceholderMessage = styled.div`
  height: 0;
  overflow: hidden;
  /* Cancel the message-list gap for zero-height placeholders so the loader can sit closer. */
  margin-block-start: ${rem(-MESSAGE_LIST_GAP_PX)};
`

const AssistantToolbarWrapper = styled.div`
  margin-block-start: ${rem(-12)};
`

const MessagesListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const LoadingIndicatorWrapper = styled.div`
  margin-block-start: ${rem(LOADING_AFTER_CONTENT_GAP_PX)};

  &.${LOADING_AFTER_PLACEHOLDER_CLASS} {
    margin-block-start: ${rem(LOADING_AFTER_PLACEHOLDER_GAP_PX)};
  }
`

const LoadingAnimationImage = styled(Image)`
  [dir="rtl"] & {
    scale: -1 1;
    transform-box: fill-box;
    transform-origin: center;
  }
`

const MessageWithDateSeparator = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  width: 100%;
`

const MessageDateSeparator = styled(BodyText)`
  width: 100%;
  text-align: center;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

export interface MessageItem {
  id: string
  parts: Array<MessagePart>
  role: 'user' | 'assistant'
  messageType?: string
  /** BE-assigned conversation sequence number -- stable identity for activity-log references, unlike a locally-recomputed position. */
  messageSeq?: number
  isMarkdown?: boolean
  files?: Array<File>
  rawContent?: string
  oaiConversationId?: string
  generationStatus?: string
  generationStartedAt?: string
  generationFinishedAt?: string
  createdAt?: string
  assistantTurnPolicySnapshot?: unknown
  isFinalized?: boolean
  /** v3: placeholder overlay — present in DOM for scroll calculations but renders as zero-height until content arrives */
  isPlaceholder?: boolean
}

interface MessagesListProps {
  messages: Array<MessageItem>
  chatId?: number
  isLoading?: boolean
  isUpdatingItinerary?: boolean
  /** Toolbar callbacks — consumed by AssistantResponseToolbar */
  onRegenerate?: () => void
  onCopy?: () => void
  disableRegenerateButton?: boolean
  disableCopyButton?: boolean
  showAssistantActions?: boolean
  showRegenerateButton?: boolean
  isStreaming?: boolean
  copiedRecently?: boolean
  onThumbsUp?: () => void
  onThumbsDown?: () => void
  feedbackSubmitted?: 'UP' | 'DOWN'
  /**
   * Live multi-choice selection from the page.
   * Applied to the last message only; older messages use their
   * historical selection computed from message content.
   */
  externalSelection?: Array<string>
  activeToolLoadingKey?: ChatToolLoadingKey
  /** Stream active but no chunks received for 10+ seconds. */
  isStalled?: boolean
  /** Device network status via navigator.onLine. */
  isOnline?: boolean
  /** Cycling summary messages from Groq for the reasoning phase. */
  reasoningSummaries?: ReadonlyArray<string>
  /** Current tool call info for the tool_calling phase. */
  toolCallInfo?: { tool: string; args: Record<string, unknown> }
  /** Current loading status for phase-specific fallback labels. */
  loadingStatus?: ChatLoadingStatus
  /** Enables Trip Planner-only message actions such as add-to-trip controls. */
  showTripPlannerActions?: boolean
  /** Opens offer carousel cards as new-tab links instead of Trip Chat drawers. */
  openOfferLinksInNewTab?: boolean
  FlightSearchDrawerComponent?: ComponentType<FlightSearchDrawerComponentProps>
  CartComponent?: ComponentType<ChatCartComponentProps>
  /** Applies private-chat treatment to user messages. */
  isPrivateChat?: boolean
}

interface MessageMetadata {
  previousRole: 'user' | 'assistant' | undefined
  isLastMessage: boolean
  multiChoiceInitialSelection: Array<string> | undefined
  previousChoiceOptions: Array<string> | undefined
  confirmationPromptActionSelection: 'confirm' | 'cancel' | undefined
  dateSeparatorLabel: string | undefined
  plainAssistantUi: boolean
  hideAskAIContextLabel: boolean
}

function getConfirmationPromptPendingActionId(parts: Array<MessagePart>): string | undefined {
  for (const part of parts) {
    if (
      part.type === PART_TYPES.UI_COMPONENT &&
      part.data.type === UI_COMPONENT_TYPES.CONFIRMATION_PROMPT
    ) {
      return part.data.pendingActionId
    }
  }

  return undefined
}

function parseConfirmationPromptActionSelection(
  userContent: string,
  pendingActionId: string | undefined,
): 'confirm' | 'cancel' | undefined {
  if (!pendingActionId) {
    return undefined
  }

  const visibleContent = stripContextTags(userContent).trim()
  // Exact typed replies should collapse the matching prompt in the same way
  // as clicking the built-in button.

  if (
    userContent.includes(`Confirm staged action pending_action_id=${pendingActionId}`)
    || visibleContent === 'Confirm'
  ) {
    return 'confirm'
  }

  if (
    userContent.includes(`Cancel staged action pending_action_id=${pendingActionId}`)
    || visibleContent === 'Cancel'
  ) {
    return 'cancel'
  }

  return undefined
}

function getChoiceOptions(parts: Array<MessagePart>): Array<string> {
  const allOptions: Array<string> = []
  for (const part of parts) {
    if (
      part.type === PART_TYPES.UI_COMPONENT &&
      (part.data.type === UI_COMPONENT_TYPES.PILLS_LIST ||
        part.data.type === UI_COMPONENT_TYPES.CARDS_LIST)
    ) {
      const options = part.data.options.map((opt: { label: string } | { title: string }) => {
        return 'label' in opt ? opt.label : opt.title
      })
      allOptions.push(...options)
    }
  }
  return allOptions
}

function parseSelectedLabels(userContent: string, options: Array<string>): Array<string> {
  return options.filter(label => {
    const pattern = new RegExp(`(^|\\s|,|\\n)${escapeRegex(label)}(\\s|,|\\n|$)`, 'i')
    return pattern.test(userContent)
  })
}

function getMessageDateKey(createdAt?: string): string | undefined {
  if (!createdAt) return undefined

  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return undefined

  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

function formatMessageDateLabel(createdAt: string | undefined, intl: IntlShape): string | undefined {
  if (!createdAt) return undefined

  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return undefined

  return intlDateFormatter(date, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale)
}

function isLoaderOnlyAssistantMessage(message: MessageItem, isLoading: boolean): boolean {
  if (message.role !== MESSAGE_ROLES.ASSISTANT) return false
  if (message.isPlaceholder) return true
  return isLoading && !hasAssistantReplyContentStarted(message)
}

function shouldHideDateSeparators(messages: Array<MessageItem>): boolean {
  const datedMessages = messages.filter(message => getMessageDateKey(message.createdAt) !== undefined)
  if (datedMessages.length === 0) return false

  const todayDateKey = getMessageDateKey(new Date().toISOString())
  if (!todayDateKey) return false

  return datedMessages.every(message => getMessageDateKey(message.createdAt) === todayDateKey)
}

function getMessageTextContent(message: MessageItem | undefined): string | undefined {
  if (!message) return undefined

  if (message.rawContent) return message.rawContent

  return message.parts
    .filter((part): part is Extract<MessagePart, { type: 'text' }> => part.type === PART_TYPES.TEXT)
    .map(textPart => textPart.content)
    .join('')
}

function buildMessageMetadata(messages: Array<MessageItem>, intl: IntlShape): Array<MessageMetadata> {
  const hideDateSeparators = shouldHideDateSeparators(messages)

  return messages.map((message, messageIndex) => {
    const previousMessage = messageIndex > 0 ? messages[messageIndex - 1] : undefined
    const previousRole = previousMessage?.role
    const isLastMessage = messageIndex === messages.length - 1
    const currentDateKey = getMessageDateKey(message.createdAt)
    const previousDateKey = getMessageDateKey(previousMessage?.createdAt)
    const dateSeparatorLabel = !hideDateSeparators && currentDateKey && currentDateKey !== previousDateKey
      ? formatMessageDateLabel(message.createdAt, intl)
      : undefined
    let multiChoiceInitialSelection: Array<string> | undefined
    let confirmationPromptActionSelection: 'confirm' | 'cancel' | undefined

    if (message.role === MESSAGE_ROLES.ASSISTANT && !isLastMessage) {
      const nextMessage = messages[messageIndex + 1]
      if (nextMessage?.role === 'user') {
        const userContent = getMessageTextContent(nextMessage) ?? ''
        const options = getChoiceOptions(message.parts)
        if (options.length > 0) {
          multiChoiceInitialSelection = parseSelectedLabels(stripContextTags(userContent), options)
        }
        confirmationPromptActionSelection = parseConfirmationPromptActionSelection(
          userContent,
          getConfirmationPromptPendingActionId(message.parts),
        )
      }
    }

    const previousChoiceOptions =
      message.role === MESSAGE_ROLES.USER && previousRole === MESSAGE_ROLES.ASSISTANT
        ? getChoiceOptions(previousMessage?.parts ?? [])
        : undefined

    const previousUserContent = previousRole === MESSAGE_ROLES.USER
      ? getMessageTextContent(previousMessage)
      : undefined
    const currentUserContent = message.role === MESSAGE_ROLES.USER
      ? getMessageTextContent(message)
      : undefined

    return {
      previousRole,
      isLastMessage,
      multiChoiceInitialSelection,
      previousChoiceOptions,
      confirmationPromptActionSelection,
      dateSeparatorLabel,
      plainAssistantUi: message.role === MESSAGE_ROLES.ASSISTANT
        && userMessagePrefersPlainAssistantUi(previousUserContent),
      hideAskAIContextLabel: message.role === MESSAGE_ROLES.USER
        && userMessagePrefersPlainAssistantUi(currentUserContent),
    }
  })
}

function useCyclingSummaries(summaries: ReadonlyArray<string>, intervalMs = 5000): string | undefined {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
    if (summaries.length <= 1) return
    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % summaries.length)
    }, intervalMs)
    return () => clearInterval(id)
  }, [summaries, intervalMs])

  if (summaries.length === 0) return undefined
  return summaries[index % summaries.length]
}

export function formatToolCallLabel(
  tool: string,
  args: Record<string, unknown>,
  intl: IntlShape,
): string {
  // naturalLanguageQuery is the model's search query — already a user-friendly description
  // like "romantic hotels in Nusa Dua Bali". Use it directly as the label, prefixed with
  // "Searching for". Don't embed it in "Searching for hotels in {query}" which doubles up.
  const query = typeof args.naturalLanguageQuery === 'string' ? args.naturalLanguageQuery : ''

  switch (tool) {
    case TOOL_NAME.SEARCH_HOTELS:
      return query
        ? intl.formatMessage({ id: 'zIE_2P8AJ2eqM2d1hwT2', defaultMessage: 'Searching for {query}\u2026', description: 'Loading label when AI is searching for hotels with a specific query in trip planner chat' }, { query })
        : intl.formatMessage({ id: 'DJzTyh_Cm-_h6NCjCUmI', defaultMessage: 'Searching for hotels\u2026', description: 'Loading label when AI is searching for hotels without a specific query in trip planner chat' })
    case TOOL_NAME.GET_SIMILAR_OFFERS:
      return intl.formatMessage({ id: 'oG-vrmvJd78TzUenJaei', defaultMessage: 'Finding similar offers\u2026', description: 'Loading label when AI is finding similar hotel offers in trip planner chat' })
    case TOOL_NAME.GET_FLASH_LEAD_PRICES:
      return intl.formatMessage({ id: 'fbTAOgeHQySRXmGmL_2Y', defaultMessage: 'Checking the best hotel offers\u2026', description: 'Loading label when AI is checking flash hotel lead prices in trip planner chat' })
    case TOOL_NAME.SEARCH_EXPERIENCES:
      return query
        ? intl.formatMessage({ id: 'sEyzTQylegUCkZA-FdYx', defaultMessage: 'Searching for {query}\u2026', description: 'Loading label when AI is searching for experiences with a specific query in trip planner chat' }, { query })
        : intl.formatMessage({ id: 'NxKjXbGX7AGf3taYwvC5', defaultMessage: 'Finding experiences\u2026', description: 'Loading label when AI is searching for experiences without a specific query in trip planner chat' })
    case TOOL_NAME.SEARCH_CRUISES:
      return intl.formatMessage({ id: 'vQa2lZDbQzWwMOHSBifJ', defaultMessage: 'Searching for the best cruises\u2026', description: 'Loading label when AI is searching cruises in trip planner chat' })
    case TOOL_NAME.SEARCH_AIRPORT_TRANSFERS:
      return intl.formatMessage({ id: 'WY4FUR7KkIcCewG8drCv', defaultMessage: 'Searching for airport transfers\u2026', description: 'Loading label when AI is searching for airport transfers in trip planner chat' })
    case TOOL_NAME.SEARCH_AIRPORT_LOUNGE:
      return intl.formatMessage({ id: 'AfkNgJLwsp5C_ZPWAs9d', defaultMessage: 'Searching for airport lounges\u2026', description: 'Loading label when AI is searching for airport lounges in trip planner chat' })
    case TOOL_NAME.SEARCH_OFFER_REVIEWS:
      return intl.formatMessage({ id: '4myythEyQzDSV86pGOn0', defaultMessage: 'Searching reviews\u2026', description: 'Loading label when AI is searching reviews for a hotel or experience in trip planner chat' })
    case TOOL_NAME.SEARCH_FLIGHT_SALES:
      return intl.formatMessage({ id: 'i2Y_SA3SYuJockRbKYUb', defaultMessage: 'Checking flight sales\u2026', description: 'Loading label when AI is checking flight sale campaigns and route deals in trip planner chat' })
    case TOOL_NAME.SEARCH_FLIGHTS:
      return intl.formatMessage({ id: '8wdm_RdhsHFMyJbPaXDV', defaultMessage: 'Looking up flights\u2026', description: 'Loading label when AI is searching for flights in trip planner chat' })
    case TOOL_NAME.SEARCH_FLIGHT_CALENDAR_GRID:
      return intl.formatMessage({ id: 'NfOluzxycoalYZhHLId1', defaultMessage: 'Checking flexible flight dates\u2026', description: 'Loading label when AI is checking flexible flight date prices in trip planner chat' })
    case TOOL_NAME.GET_ORDERS:
      return intl.formatMessage({ id: '1irTo-3lMOEBMw4yiVVT', defaultMessage: 'Looking through your order history\u2026', description: 'Loading label when AI is looking through the user order history in trip planner chat' })
    case TOOL_NAME.GET_ORDER_DETAILS:
      return intl.formatMessage({ id: 'N1Wv3_BPjFFEn-Vysho3', defaultMessage: 'Looking up your booking details\u2026', description: 'Loading label when AI is looking up details for a specific user order in trip planner chat' })
    case TOOL_NAME.GET_ITINERARY:
      return intl.formatMessage({ id: 'qaoNa65eiNPkPcAnhmI0', defaultMessage: 'Checking your itinerary\u2026', description: 'Loading label when AI is checking the current trip itinerary in trip planner chat' })
    case TOOL_NAME.UPDATE_ITINERARY:
      return intl.formatMessage({ id: 'LaWagDiNfURFcAfbmWgw', defaultMessage: 'Updating your itinerary\u2026', description: 'Loading label when AI is updating the current trip itinerary in trip planner chat' })
    case TOOL_NAME.GET_CUSTOMER_SHOPPING_CARTS:
      return intl.formatMessage({ id: 'KI6PCjH68iggqI4drnbt', defaultMessage: 'Checking your shopping carts\u2026', description: 'Loading label when AI is checking the user shopping carts in trip planner chat' })
    case TOOL_NAME.GET_CUSTOMER_WALLET:
      return intl.formatMessage({ id: 'E0A_EGWbhte-DPvHex5M', defaultMessage: 'Checking your profile\u2026', description: 'Loading label when AI is checking the user wallet, loyalty, membership, credits, and vouchers in trip planner chat' })
    case TOOL_NAME.GET_COMMUNICATIONS_PREFERENCES:
      return intl.formatMessage({ id: 'zVmEm3AajneTlSB3Ph8B', defaultMessage: 'Checking your communications preferences\u2026', description: 'Loading label when AI is checking the user communication preferences in trip planner chat' })
    case TOOL_NAME.GET_RECENT_CUSTOMER_EMAILS:
      return intl.formatMessage({ id: 'NLdWEIZwH18275hT1TBS', defaultMessage: 'Reading your recent emails\u2026', description: 'Loading label when AI is reading the user recent email archive in trip planner chat' })
    case TOOL_NAME.LOOKUP_OFFER:
      return intl.formatMessage({ id: '1QWQulSeYQUQkM4SvjVW', defaultMessage: 'Looking up offer details\u2026', description: 'Loading label when AI is looking up a specific offer in trip planner chat' })
    case TOOL_NAME.GET_OFFER_REVIEWS:
      return intl.formatMessage({ id: 'ebmZeVTxguWsK-a1E0TM', defaultMessage: 'Reading recent reviews\u2026', description: 'Loading label when AI is reading hotel or experience reviews in trip planner chat' })
    case TOOL_NAME.RESEARCH_SUPPORT_ARTICLES:
      return intl.formatMessage({ id: '3-aM-JHqVsk84gHMm1Wh', defaultMessage: 'Searching support articles\u2026', description: 'Loading label when AI is searching customer support knowledge articles in trip planner chat' })
    case TOOL_NAME.GET_WEATHER_FORECAST:
      return intl.formatMessage({ id: 'uGvwFX3wLMha9YwLcnNa', defaultMessage: 'Checking the weather forecast\u2026', description: 'Loading label when AI is checking the current weather and daily forecast in trip planner chat' })
    case TOOL_NAME.GET_CURRENCY_EXCHANGE_RATES:
      return intl.formatMessage({ id: 'c_Rw2fvZkZkh3kAPGHKu', defaultMessage: 'Checking currency rates\u2026', description: 'Loading label when AI is checking current currency exchange rates in trip planner chat' })
    case TOOL_NAME.LOOKUP_GOOGLE_PLACES:
      return intl.formatMessage({ id: 'kYJRALcQ9_2nlVIWMw5n', defaultMessage: 'Looking up places\u2026', description: 'Loading label when AI is searching Google Places for real-world place information in trip planner chat' })
    case TOOL_NAME.LOOKUP_OFFER_GENERAL_AVAILABILITY:
      return intl.formatMessage({ id: '7xsHWyix6HjMwlgHuVlc', defaultMessage: 'Checking broad availability\u2026', description: 'Loading label when AI is checking broad hotel or experience availability in trip planner chat' })
    case TOOL_NAME.LOOKUP_OFFER_SPECIFIC_AVAILABILITY:
      return intl.formatMessage({ id: 'VHAFyRwISyGY6D4QYFsC', defaultMessage: 'Checking exact availability\u2026', description: 'Loading label when AI is checking exact hotel or experience availability in trip planner chat' })
    case TOOL_NAME.GET_CHAT_NAME:
      return intl.formatMessage({ id: 'NzrFl57ANte7EDgCg0iY', defaultMessage: 'Checking your chat name\u2026', description: 'Loading label when AI is checking the current trip or chat name in trip planner chat' })
    case TOOL_NAME.RENAME_CHAT:
      return intl.formatMessage({ id: 'EuEQz8MPvkWwl-4rgeB1', defaultMessage: 'Renaming your chat\u2026', description: 'Loading label when AI is renaming the current trip or chat in trip planner chat' })
    case TOOL_NAME.SUBMIT_WEDDING_INQUIRY:
      return intl.formatMessage({ id: 'sgpyrVmXtGmNzjxgZHX9', defaultMessage: 'Preparing your wedding enquiry\u2026', description: 'Loading label when AI is preparing the user destination wedding concierge enquiry in trip planner chat' })
    default:
      return intl.formatMessage({ id: '6c5xn35ovrdi7Qqa22_o', defaultMessage: 'Working on it\u2026', description: 'Generic loading label when AI is executing an unknown tool in trip planner chat' })
  }
}

function MessagesList({
  messages,
  chatId,
  isLoading = false,
  isUpdatingItinerary = false,
  onRegenerate = noop,
  onCopy = noop,
  disableRegenerateButton = false,
  disableCopyButton = false,
  showAssistantActions = false,
  showRegenerateButton = true,
  isStreaming = false,
  copiedRecently = false,
  onThumbsUp = noop,
  onThumbsDown = noop,
  feedbackSubmitted,
  externalSelection,
  activeToolLoadingKey,
  isStalled = false,
  isOnline = true,
  reasoningSummaries = EMPTY_SUMMARIES,
  toolCallInfo,
  loadingStatus = 'idle',
  showTripPlannerActions = true,
  openOfferLinksInNewTab = false,
  FlightSearchDrawerComponent,
  CartComponent,
  isPrivateChat = false,
}: MessagesListProps) {
  const intl = useIntl()
  const { inspectedMessageId, isInspecting } = useStaffRewind()
  const displayMessages = useMemo(
    () => filterMessagesUpToStaffRewindPoint(messages, inspectedMessageId),
    [messages, inspectedMessageId],
  )
  const isThumbsUp = feedbackSubmitted === 'UP'
  const isThumbsDown = feedbackSubmitted === 'DOWN'
  const isThumbsIconDisabled = feedbackSubmitted !== undefined
  const currentSummary = useCyclingSummaries(reasoningSummaries)

  const toolCallLabel = useMemo(() => {
    if (!toolCallInfo) return undefined
    return formatToolCallLabel(toolCallInfo.tool, toolCallInfo.args, intl)
  }, [toolCallInfo, intl])

  const connectionStatusLabel = useMemo(() => {
    if (!isOnline) {
      return intl.formatMessage({
        id: 'p7Quu-YJBMFaMHUl1gYI',
        defaultMessage: "You're offline — will retry when connected",
        description: 'Loading indicator label when device has no network connection during chat streaming',
      })
    }
    if (isStalled) {
      return intl.formatMessage({
        id: 'LJCUCz9svpwcnjXJ0-Oc',
        defaultMessage: 'Taking longer than expected\u2026',
        description: 'Loading indicator label when the AI stream has stalled (no data for 10+ seconds)',
      })
    }
    return undefined
  }, [isOnline, isStalled, intl])

  const toolLoadingLabel = useMemo(() => {
    if (!activeToolLoadingKey) return undefined
    return intl.formatMessage(
      getChatToolLoadingMessageDescriptor(activeToolLoadingKey),
    )
  }, [activeToolLoadingKey, intl])

  const reasoningFallbackLabel = useMemo(() => {
    if (loadingStatus !== 'reasoning' || currentSummary) return undefined
    return intl.formatMessage({
      id: 'lj2_OxUq7xoaw2FGp47h',
      defaultMessage: 'Thinking\u2026',
      description: 'Fallback loading label during AI reasoning when Groq summaries are unavailable in trip planner chat',
    })
  }, [loadingStatus, currentSummary, intl])

  const messageMetadata = useMemo(() => buildMessageMetadata(displayMessages, intl), [displayMessages, intl])

  const loadingLabel = connectionStatusLabel ?? toolCallLabel ?? currentSummary ?? reasoningFallbackLabel ?? toolLoadingLabel
  const lastMessage = displayMessages.at(-1)
  const trailingIsLoaderOnly = Boolean(
    lastMessage && isLoaderOnlyAssistantMessage(lastMessage, isLoading),
  )
  const loadingIndicatorClassName = trailingIsLoaderOnly
    ? LOADING_AFTER_PLACEHOLDER_CLASS
    : undefined

  return (
    <MessagesListWrapper>
      <StaffRewindBanner />
      <Group direction="vertical" gap={MESSAGE_LIST_GAP_PX} fullWidth>
        {
          displayMessages.map((message, messageIndex) => {
            const {
              isLastMessage,
              multiChoiceInitialSelection,
              previousChoiceOptions,
              confirmationPromptActionSelection,
              dateSeparatorLabel,
              plainAssistantUi,
              hideAskAIContextLabel,
            } = messageMetadata[messageIndex]
            const renderAsPlaceholder = message.isPlaceholder || (
              !isInspecting
              && message.id === lastMessage?.id
              && isLoaderOnlyAssistantMessage(message, isLoading)
            )

            if (renderAsPlaceholder) {
              return <PlaceholderMessage key={message.id} data-message-id={message.id} />
            }

            return (
              <MessageWithDateSeparator key={message.id}>
                {dateSeparatorLabel && (
                  <MessageDateSeparator variant="small">
                    {dateSeparatorLabel}
                  </MessageDateSeparator>
                )}
                <Message
                  messageId={message.id}
                  parts={message.parts}
                  role={message.role}
                  isMarkdown={message.isMarkdown}
                  files={message.files}
                  rawContent={message.rawContent}
                  oaiConversationId={message.oaiConversationId}
                  generationStatus={message.generationStatus}
                  generationStartedAt={message.generationStartedAt}
                  generationFinishedAt={message.generationFinishedAt}
                  chatId={chatId}
                  isChatStreaming={isStreaming}
                  isFinalized={message.isFinalized}
                  isLastMessage={isLastMessage}
                  multiChoiceInitialSelection={multiChoiceInitialSelection}
                  externalSelection={externalSelection}
                  previousChoiceOptions={previousChoiceOptions}
                  confirmationPromptActionSelection={confirmationPromptActionSelection}
                  showTripPlannerActions={showTripPlannerActions}
                  openOfferLinksInNewTab={openOfferLinksInNewTab}
                  FlightSearchDrawerComponent={FlightSearchDrawerComponent}
                  CartComponent={CartComponent}
                  isPrivateChat={isPrivateChat}
                  plainAssistantUi={plainAssistantUi}
                  hideAskAIContextLabel={hideAskAIContextLabel}
                />
                {message.role === MESSAGE_ROLES.ASSISTANT && (
                  <AddedItineraryItemLink messageId={message.id} />
                )}
                {message.role === MESSAGE_ROLES.ASSISTANT && message.isFinalized && (
                  <StaffMessageRewindControls messageId={message.id} />
                )}
              </MessageWithDateSeparator>
            )
          })
        }
        {showAssistantActions && !isInspecting &&
          <AssistantToolbarWrapper>
            <AssistantResponseToolbar
              onCopy={onCopy}
              disableCopyButton={disableCopyButton}
              copiedRecently={copiedRecently}
              showRegenerateButton={showRegenerateButton}
              onRegenerate={onRegenerate}
              disableRegenerateButton={disableRegenerateButton}
              onThumbsUp={onThumbsUp}
              onThumbsDown={onThumbsDown}
              isThumbsIconDisabled={isThumbsIconDisabled}
              isThumbsUp={isThumbsUp}
              isThumbsDown={isThumbsDown}
            />
          </AssistantToolbarWrapper>}
      </Group>
      {isLoading && !isInspecting && isUpdatingItinerary && (
        <LoadingIndicatorWrapper
          className={loadingIndicatorClassName}
          data-testid="chat-loading-indicator"
        >
          <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="start">
            <LoadingAnimationImage
              id="0jst326hyikkdg7pf9n"
              width={40}
              height={40}
              alt={intl.formatMessage({
                id: 'Vyc6L4HykGywzgKBGcRY',
                defaultMessage: 'Updating your itinerary',
                description: 'Loading message while AI updates the trip itinerary',
              })}
              format="gif"
            />
            <BodyText variant="medium" weight="bold">{intl.formatMessage({
              id: 'Vyc6L4HykGywzgKBGcRY',
              defaultMessage: 'Updating your itinerary',
              description: 'Loading message while AI updates the trip itinerary',
            })}
            </BodyText>
          </Group>
        </LoadingIndicatorWrapper>
      )}
      {isLoading && !isInspecting && !isUpdatingItinerary && (
        <LoadingIndicatorWrapper
          className={loadingIndicatorClassName}
          data-testid="chat-loading-indicator"
        >
          <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="start">
            <LoadingAnimationImage
              id="2cxvqkxsvq3fhnk5lsht"
              width={40}
              height={40}
              alt={loadingLabel ?? intl.formatMessage({
                id: 'zhhQhC7Y2M6v94sHcPAS',
                defaultMessage: 'AI Trip assistant is typing',
                description: 'Alt text for loading animation while AI assistant is composing a response',
              })}
              format="gif"
            />
            {loadingLabel && (
              <BodyText variant="medium" weight="bold">
                {loadingLabel}
              </BodyText>
            )}
          </Group>
        </LoadingIndicatorWrapper>
      )}
    </MessagesListWrapper>
  )
}

export default MessagesList
